import React, { useState, useEffect, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronDown, 
  faChevronRight,
  faTimes,
  faHotel,
  faUtensils,
  faCar,
  faCompass,
  faTasks,
  faMapMarkerAlt,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import { format, addDays, differenceInDays, es } from 'date-fns';
import { es as dateFnsEs } from 'date-fns/locale';
import { createBudgetLimit, deleteBudgetLimit, getBudgetLimits } from '../services/api';
import ConfirmationModal from './ConfirmationModal';
import { AuthContext } from '../contexts/AuthContext';
import { useHistory } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const SidePanelContainer = styled.div`
  width: 30%;
  height: calc(100vh - 100px);
  background-color: white;
  padding: 0;
  overflow-y: auto;
  position: fixed;
  left: 20px;
  top: 80px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transform: translateX(${props => props.isVisible ? '0' : '-120%'});
  transition: all 0.3s ease-in-out;
  z-index: 1000;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  
  ${props => props.isChecklistOpen && `
    left: 20px;
    width: 28%;
  `}
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 70vh;
    left: 0;
    top: auto;
    bottom: 0;
    border-radius: 20px 20px 0 0;
    transform: translateY(${props => props.isVisible ? '0' : '100%'});
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 80px;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const Total = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  background-color: #316C6E;
  color: white;
  font-weight: bold;
  font-size: 18px;
  border-radius: 8px;
  margin-top: 20px;
`;

const FixedTotal = styled(Total)`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  margin: 0;
  border-radius: 0 0 12px 12px;
  z-index: 1001;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
    left: 0;
  }

  ${props => props.isChecklistOpen && `
    width: calc(100% - 0px);
  `}
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #eee;
`;

const Title = styled.h2`
  color: #316C6E;
  margin: 0;
  font-size: 24px;
`;

const Section = styled.div`
  padding: 20px;
  margin-bottom: 20px;
`;

const DaySection = styled.div`
  border: 1px solid #eee;
  border-radius: 8px;
  margin-bottom: 15px;
  overflow: hidden;
`;

const DayHeader = styled.div`
  background-color: ${props => {
    if (props.isExceeded) return '#ffebee';
    return props.isExpanded ? '#f8f8f8' : 'white';
  }};
  padding: 15px 20px;
  display: flex;
  flex-direction: column;
  cursor: ${props => props.hasItems ? 'pointer' : 'default'};
  transition: background-color 0.3s ease;
  color: ${props => props.isExceeded ? '#d32f2f' : 'inherit'};

  &:hover {
    background-color: ${props => props.hasItems ? (props.isExceeded ? '#ffebee' : '#f8f8f8') : 'white'};
  }
`;

const DayHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TypeSection = styled.div`
  padding: 0 20px;
  margin: 10px 0;
`;

const TypeHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  cursor: pointer;
  background-color: ${props => {
    if (props.isExceeded) return '#ffebee';
    return props.isExpanded ? '#f5f5f5' : 'transparent';
  }};
  border-radius: 6px;
  color: ${props => props.isExceeded ? '#d32f2f' : 'inherit'};

  &:hover {
    background-color: ${props => props.isExceeded ? '#ffebee' : '#f5f5f5'};
  }
`;

const TypeHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemList = styled.div`
  padding: 10px 20px;
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px dashed #eee;
  
  &:last-child {
    border-bottom: none;
  }

  span:first-child {
    display: flex;
    align-items: center;
    
    &:before {
      content: "•";
      margin-right: 8px;
      color: #316C6E;
      font-size: 1.2em;
    }
  }
`;

const Amount = styled.span`
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  color: ${props => props.highlight ? '#316C6E' : 'inherit'};
`;

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  color: #316C6E;
`;

const TooltipText = styled.span`
  position: relative;
  cursor: help;

  &:after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    padding: 5px 10px;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1000;
  }

  &:hover:after {
    visibility: visible;
    opacity: 1;
  }
`;

const getTypeIcon = (type) => {
  switch (type) {
    case 'hotel':
      return faHotel;
    case 'restaurant':
      return faUtensils;
    case 'rent_a_car':
      return faCar;
    case 'exploration':
      return faCompass;
    case 'task':
      return faTasks;
    case 'poi':
      return faMapMarkerAlt;
    default:
      return faMapMarkerAlt;
  }
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(price);
};

const formatType = (type) => {
  switch (type) {
    case 'rent_a_car':
      return 'Rental Cars';
    case 'poi':
      return 'Point of Interest';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
  }
};

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 2px solid #eee;
`;

const Tab = styled.button`
  padding: 10px 20px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  color: ${props => props.active ? '#316C6E' : '#666'};
  border-bottom: 2px solid ${props => props.active ? '#316C6E' : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.3s ease;

  &:hover {
    color: #316C6E;
  }
`;

const TabContent = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
`;

const AdvancedSettingsContainer = styled.div`
  position: relative;
  padding: 20px 0;
`;

const AlertSection = styled.div`
  margin-bottom: 30px;
`;

const AlertTitle = styled.h3`
  color: #316C6E;
  margin-bottom: 15px;
`;

const AlertForm = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  align-items: center;
  padding: 0 20px;
`;

const Select = styled.select`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 300px;
`;

const Input = styled.input`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 100px;
`;

const SaveButton = styled.button`
  background-color: #316C6E;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${props => props.disabled ? '#ccc' : '#254F51'};
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const AlertList = styled.div`
  margin-top: 20px;
  padding: 0 20px;
`;

const AlertItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #f8f8f8;
  border-radius: 4px;
  margin-bottom: 8px;
`;

const DeleteButton = styled.button`
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #c82333;
  }
`;

const BudgetAlert = styled.span`
  font-size: 10px;
  color: #d32f2f;
  margin-top: 5px;
`;

const PremiumFeatureOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  gap: 10px;
  z-index: 1000;
`;

const LockIcon = styled(FontAwesomeIcon)`
  font-size: 2rem;
  color: #316C6E;
`;

const UpgradeLink = styled.button`
  background: #316C6E;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.3s ease;

  &:hover {
    background: #254F51;
  }
`;

const UpgradeText = styled.p`
  color: #333;
  font-size: 1.1rem;
  margin: 10px 0;
`;

const BudgetOverviewModal = ({ onClose, itinerary, bookings = [], isChecklistOpen }) => {
  const { language } = useLanguage();
  const t = translations[language].budget;
  
  const [isVisible, setIsVisible] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  const [expandedTypes, setExpandedTypes] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [dayLimit, setDayLimit] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [typeLimit, setTypeLimit] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [budgetLimits, setBudgetLimits] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [limitToDelete, setLimitToDelete] = useState(null);
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const isPremium = user?.subscription_status === 'premium';

  // Generate array of days based on itinerary start and end dates
  const days = useMemo(() => {
    if (!itinerary?.start_date || !itinerary?.end_date) return [];
    
    const start = new Date(itinerary.start_date);
    const end = new Date(itinerary.end_date);
    const dayCount = differenceInDays(end, start) + 1;
    
    return Array.from({ length: dayCount }, (_, i) => i + 1);
  }, [itinerary?.start_date, itinerary?.end_date]);

  // Add this helper function for formatting day headers
  const formatDayHeader = (day) => {
    if (!itinerary?.start_date) return `${language === 'es' ? 'Día' : 'Day'} ${day}`;
    const date = addDays(new Date(itinerary.start_date), day - 1);
    return `${language === 'es' ? 'Día' : 'Day'} ${day} - ${format(date, 'MMM d, yyyy', { locale: language === 'es' ? dateFnsEs : undefined })}`;
  };

  useEffect(() => {
    fetchBudgetLimits();
  }, []);

  const fetchBudgetLimits = async () => {
    try {
      const limits = await getBudgetLimits(itinerary.id);
      setBudgetLimits(limits);
    } catch (error) {
      console.error('Error fetching budget limits:', error);
    }
  };

  const handleSaveDayLimit = async () => {
    try {
      await createBudgetLimit(itinerary.id, {
        type: 'day',
        value: selectedDay,
        limit: parseFloat(dayLimit)
      });
      fetchBudgetLimits();
      setSelectedDay('');
      setDayLimit('');
    } catch (error) {
      console.error('Error saving day limit:', error);
    }
  };

  const handleSaveTypeLimit = async () => {
    try {
      await createBudgetLimit(itinerary.id, {
        type: 'activity_type',
        value: selectedType,
        limit: parseFloat(typeLimit)
      });
      fetchBudgetLimits();
      setSelectedType('');
      setTypeLimit('');
    } catch (error) {
      console.error('Error saving type limit:', error);
    }
  };

  const handleDeleteLimit = async (limitId) => {
    try {
      await deleteBudgetLimit(limitId);
      fetchBudgetLimits();
    } catch (error) {
      console.error('Error deleting budget limit:', error);
    }
  };

  const getDayOptions = () => {
    const options = [];
    const tripDuration = Math.ceil(
      (new Date(itinerary.end_date) - new Date(itinerary.start_date)) / (1000 * 60 * 60 * 24)
    ) + 1;

    for (let i = 1; i <= tripDuration; i++) {
      options.push(
        <option key={i} value={i}>
          {formatDayHeader(i)}
        </option>
      );
    }
    return options;
  };

  const activityTypes = [
    { value: 'hotel', label: 'Hotels' },
    { value: 'restaurant', label: 'Restaurants' },
    { value: 'rent_a_car', label: 'Rental Cars' },
    { value: 'exploration', label: 'Exploration' },
    { value: 'task', label: 'Tasks' },
    { value: 'poi', label: 'Points of Interest' }
  ];

  const toggleDay = (day) => {
    setExpandedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const toggleType = (day, type) => {
    const key = `${day}-${type}`;
    setExpandedTypes(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const calculateBudget = () => {
    const budget = {
      days: {},
      total: 0
    };

    if (itinerary?.start_date && itinerary?.end_date) {
      const start = new Date(itinerary.start_date);
      const end = new Date(itinerary.end_date);
      const tripDuration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

      for (let i = 1; i <= tripDuration; i++) {
        budget.days[i] = {
          total: 0,
          types: {}
        };
      }
    }

    if (itinerary?.pois?.length > 0) {
      itinerary.pois.forEach(poi => {
        if (poi.price) {
          const day = poi.day || 1;
          if (!budget.days[day]) {
            budget.days[day] = {
              total: 0,
              types: {}
            };
          }
          const poiType = poi.type || 'poi';
          if (!budget.days[day].types[poiType]) {
            budget.days[day].types[poiType] = {
              total: 0,
              items: []
            };
          }
          budget.days[day].types[poiType].items.push({
            name: poi.name,
            price: poi.price
          });
          budget.days[day].types[poiType].total += poi.price;
          budget.days[day].total += poi.price;
          budget.total += poi.price;
        }
      });
    }

    if (itinerary?.Bookings?.length > 0) {
      itinerary.Bookings.forEach(booking => {
        const day = 1; // Assuming booking starts on day 1
        if (!budget.days[day]) {
          budget.days[day] = {
            total: 0,
            types: {}
          };
        }
        if (!budget.days[day].types['hotel']) {
          budget.days[day].types['hotel'] = {
            total: 0,
            items: []
          };
        }
        budget.days[day].types['hotel'].items.push({
          name: `${booking.hotelName} - ${booking.roomType}`,
          price: booking.price || booking.totalAmount,
          hasBookingFee: true
        });
        const amount = booking.price || booking.totalAmount;
        budget.days[day].types['hotel'].total += amount;
        budget.days[day].total += amount;
        budget.total += amount;
      });
    }

    if (bookings?.length > 0) {
      bookings.forEach(booking => {
        const amount = booking.price || booking.totalAmount;
        if (!amount) return;

        const day = 1; // Assuming booking starts on day 1
        if (!budget.days[day]) {
          budget.days[day] = {
            total: 0,
            types: {}
          };
        }
        if (!budget.days[day].types['hotel']) {
          budget.days[day].types['hotel'] = {
            total: 0,
            items: []
          };
        }
        budget.days[day].types['hotel'].items.push({
          name: `${booking.hotelName} - ${booking.roomType}`,
          price: amount,
          hasBookingFee: true
        });
        budget.days[day].types['hotel'].total += amount;
        budget.days[day].total += amount;
        budget.total += amount;
      });
    }

    return budget;
  };

  const budget = calculateBudget();

  const isDayLimitValid = selectedDay && dayLimit && parseFloat(dayLimit) > 0;
  const isTypeLimitValid = selectedType && typeLimit && parseFloat(typeLimit) > 0;

  const getAvailableDays = () => {
    const usedDays = new Set(
      budgetLimits
        .filter(limit => limit.type === 'day')
        .map(limit => limit.value)
    );

    const tripDuration = Math.ceil(
      (new Date(itinerary.end_date) - new Date(itinerary.start_date)) / (1000 * 60 * 60 * 24)
    ) + 1;

    const options = [];
    for (let i = 1; i <= tripDuration; i++) {
      if (!usedDays.has(i.toString())) {
        options.push(
          <option key={i} value={i}>
            {formatDayHeader(i)}
          </option>
        );
      }
    }
    return options;
  };

  const getAvailableTypes = () => {
    const usedTypes = new Set(
      budgetLimits
        .filter(limit => limit.type === 'activity_type')
        .map(limit => limit.value)
    );

    return activityTypes.filter(type => !usedTypes.has(type.value));
  };

  const handleDeleteClick = (limitId) => {
    setLimitToDelete(limitId);
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (limitToDelete) {
      await handleDeleteLimit(limitToDelete);
      setShowConfirmDelete(false);
      setLimitToDelete(null);
    }
  };

  const checkBudgetLimit = (type, value, amount) => {
    const limit = budgetLimits.find(
      limit => limit.type === type && limit.value === value.toString()
    );
    if (!limit) return null;
    return amount > parseFloat(limit.amount) ? limit.amount : null;
  };

  const hasDayItems = (dayData) => {
    return Object.keys(dayData.types).length > 0;
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  useEffect(() => {
    let touchStartY = 0;
    let touchEndY = 0;
    
    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };
    
    const handleTouchMove = (e) => {
      touchEndY = e.touches[0].clientY;
    };
    
    const handleTouchEnd = () => {
      const swipeDistance = touchEndY - touchStartY;
      if (swipeDistance > 100) {
        handleClose();
      }
    };
    
    if (window.innerWidth <= 768) {
      const element = document.getElementById('budget-panel');
      if (element) {
        element.addEventListener('touchstart', handleTouchStart);
        element.addEventListener('touchmove', handleTouchMove);
        element.addEventListener('touchend', handleTouchEnd);
      }
      
      return () => {
        if (element) {
          element.removeEventListener('touchstart', handleTouchStart);
          element.removeEventListener('touchmove', handleTouchMove);
          element.removeEventListener('touchend', handleTouchEnd);
        }
      };
    }
  }, []);

  const handleUpgradeClick = () => {
    history.push('/subscription');
  };

  return (
    <SidePanelContainer 
      isVisible={isVisible} 
      id="budget-panel"
      isChecklistOpen={isChecklistOpen}
    >
      <CloseButton onClick={handleClose}>
        <FontAwesomeIcon icon={faTimes} />
      </CloseButton>

      <Header>
        <Title>{t.title}</Title>
      </Header>

      <TabContainer>
        <Tab 
          active={activeTab === 'overview'} 
          onClick={() => setActiveTab('overview')}
        >
          {t.overview}
        </Tab>
        <Tab 
          active={activeTab === 'advanced'} 
          onClick={() => setActiveTab('advanced')}
        >
          {t.advancedSettings}
        </Tab>
      </TabContainer>

      <ScrollableContent>
        <TabContent active={activeTab === 'overview'}>
          <Section>
            {Object.entries(budget.days).map(([day, dayData]) => {
              const dayLimit = checkBudgetLimit('day', day, dayData.total);
              const isDayExceeded = dayLimit !== null;
              const hasItems = hasDayItems(dayData);

              return (
                <DaySection key={day}>
                  <DayHeader 
                    onClick={() => hasItems && toggleDay(day)}
                    isExpanded={expandedDays[day]}
                    hasItems={hasItems}
                    isExceeded={isDayExceeded}
                  >
                    <DayHeaderContent>
                      <span>{formatDayHeader(parseInt(day))}</span>
                      <div>
                        <Amount bold>{formatPrice(dayData.total)}</Amount>
                        {hasItems && (
                          <FontAwesomeIcon 
                            icon={expandedDays[day] ? faChevronDown : faChevronRight} 
                            style={{ marginLeft: '10px' }}
                          />
                        )}
                      </div>
                    </DayHeaderContent>
                    {isDayExceeded && (
                      <BudgetAlert>
                        Budget exceeded (Limit: ${dayLimit})
                      </BudgetAlert>
                    )}
                  </DayHeader>

                  {hasItems && expandedDays[day] && Object.entries(dayData.types).map(([type, typeData]) => {
                    const typeLimit = checkBudgetLimit('activity_type', type, typeData.total);
                    const isTypeExceeded = typeLimit !== null;

                    return (
                      <TypeSection key={type}>
                        <TypeHeader 
                          onClick={() => toggleType(day, type)}
                          isExpanded={expandedTypes[`${day}-${type}`]}
                          isExceeded={isTypeExceeded}
                        >
                          <TypeHeaderContent>
                            <div>
                              <IconWrapper>
                                <FontAwesomeIcon icon={getTypeIcon(type)} />
                              </IconWrapper>
                              <span>{formatType(type)}</span>
                            </div>
                            <div>
                              <Amount highlight>{formatPrice(typeData.total)}</Amount>
                              <FontAwesomeIcon 
                                icon={expandedTypes[`${day}-${type}`] ? faChevronDown : faChevronRight} 
                                style={{ marginLeft: '10px' }}
                              />
                            </div>
                          </TypeHeaderContent>
                          {isTypeExceeded && (
                            <BudgetAlert>
                              Budget exceeded (Limit: ${typeLimit})
                            </BudgetAlert>
                          )}
                        </TypeHeader>

                        {expandedTypes[`${day}-${type}`] && (
                          <ItemList>
                            {typeData.items.map((item, index) => (
                              <Item key={index}>
                                <span>
                                  {item.name}
                                  {item.hasBookingFee && (
                                    <TooltipText data-tooltip="Price includes booking fees and taxes">
                                      *
                                    </TooltipText>
                                  )}
                                </span>
                                <Amount>{formatPrice(item.price)}</Amount>
                              </Item>
                            ))}
                          </ItemList>
                        )}
                      </TypeSection>
                    );
                  })}
                </DaySection>
              );
            })}
          </Section>
        </TabContent>

        <TabContent active={activeTab === 'advanced'}>
          <AdvancedSettingsContainer>
            <Section>
              <h3>{t.budgetAlerts}</h3>
              <AlertForm>
                <Select 
                  value={selectedDay} 
                  onChange={(e) => setSelectedDay(e.target.value)}
                  disabled={!isPremium}
                >
                  <option value="">{t.selectDay}</option>
                  {days.map(day => (
                    <option key={day} value={day}>
                      {formatDayHeader(day)}
                    </option>
                  ))}
                </Select>
                <Input
                  type="number"
                  placeholder={t.limit}
                  value={dayLimit}
                  onChange={(e) => setDayLimit(e.target.value)}
                  disabled={!isPremium}
                />
                <SaveButton 
                  onClick={handleSaveDayLimit}
                  disabled={!isPremium || !selectedDay || !dayLimit}
                >
                  {t.set}
                </SaveButton>
              </AlertForm>

              <AlertForm>
                <Select 
                  value={selectedType} 
                  onChange={(e) => setSelectedType(e.target.value)}
                  disabled={!isPremium}
                >
                  <option value="">{t.selectType}</option>
                  {activityTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Select>
                <Input
                  type="number"
                  placeholder={t.limit}
                  value={typeLimit}
                  onChange={(e) => setTypeLimit(e.target.value)}
                  disabled={!isPremium}
                />
                <SaveButton 
                  onClick={handleSaveTypeLimit}
                  disabled={!isPremium || !selectedType || !typeLimit}
                >
                  {t.set}
                </SaveButton>
              </AlertForm>

              <AlertList>
                {budgetLimits.map(limit => (
                  <AlertItem key={limit.id}>
                    <span>
                      {limit.type === 'day' 
                        ? `${formatDayHeader(limit.value)}: $${limit.amount}`
                        : `${activityTypes.find(t => t.value === limit.value)?.label}: $${limit.amount}`
                      }
                    </span>
                    <DeleteButton onClick={() => handleDeleteClick(limit.id)}>
                      {t.delete}
                    </DeleteButton>
                  </AlertItem>
                ))}
              </AlertList>

              {!isPremium && (
                <PremiumFeatureOverlay>
                  <LockIcon icon={faLock} />
                  <UpgradeText>
                    {t.premiumFeature.title}
                  </UpgradeText>
                  <UpgradeLink onClick={handleUpgradeClick}>
                    {t.premiumFeature.upgrade}
                  </UpgradeLink>
                </PremiumFeatureOverlay>
              )}
            </Section>
          </AdvancedSettingsContainer>
        </TabContent>
      </ScrollableContent>

      <FixedTotal isChecklistOpen={isChecklistOpen}>
        <span>{t.totalBudget}</span>
        <span>{formatPrice(budget.total)}</span>
      </FixedTotal>

      {showConfirmDelete && (
        <ConfirmationModal
          isOpen={showConfirmDelete}
          onClose={() => setShowConfirmDelete(false)}
          onConfirm={handleConfirmDelete}
          title={t.confirmDelete.title}
          message={t.confirmDelete.message}
        />
      )}
    </SidePanelContainer>
  );
};

export default BudgetOverviewModal; 